/**
 * Restaurant printing helpers — 80mm kitchen tickets (routed per station) and
 * customer receipts. Two transports:
 *   - "browser": open an 80mm print window → goes to the device's default printer.
 *   - "usb": ESC/POS bytes written straight to a USB serial printer bound to the station.
 * Per-station printer assignment is stored on the device (localStorage).
 */
import { encoder, isSupported as serialSupported, writeBytesTo } from "@/lib/thermalPrinter";

const KEY = (cid) => `rest_station_printers_${cid || "x"}`;
export const getStationPrinters = (cid) => {
  try { return JSON.parse(localStorage.getItem(KEY(cid)) || "{}"); } catch { return {}; }
};
export const setStationPrinter = (cid, stationKey, cfg) => {
  const all = getStationPrinters(cid);
  all[stationKey] = cfg;
  localStorage.setItem(KEY(cid), JSON.stringify(all));
};

const typeLabel = (t) => ({ dine_in: "صالة", takeaway: "سفري", delivery: "توصيل" }[t] || "");

function openPrintWindow(title, bodyHtml) {
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) { alert("فعّل النوافذ المنبثقة (Popups) للسماح بالطباعة"); return; }
  w.document.write(`<html dir="rtl"><head><meta charset="utf-8"><title>${title}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    * { font-family: 'Tahoma','Arial',sans-serif; }
    body { width: 80mm; margin: 0; padding: 6px 8px; color: #000; }
    .c { text-align: center; } .b { font-weight: bold; }
    .lg { font-size: 19px; } .md { font-size: 14px; } .sm { font-size: 11px; }
    hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 2px 0; font-size: 13px; vertical-align: top; }
    .qty { width: 36px; font-weight: bold; }
    .note { font-size: 11px; color: #333; font-style: italic; }
    .rt { text-align: left; white-space: nowrap; }
  </style></head><body>${bodyHtml}</body></html>`);
  w.document.close();
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 350);
}

export function kitchenTicketHtml(t) {
  const head = t.order_type === "dine_in" ? `طاولة ${t.table_number || "-"}` : typeLabel(t.order_type);
  const items = (t.items || []).map((it) =>
    `<tr><td class="qty">${it.qty}×</td><td>${it.name}${it.notes ? `<div class="note">↳ ${it.notes}</div>` : ""}</td></tr>`
  ).join("");
  const time = new Date(t.created_at || Date.now()).toLocaleString("ar-EG");
  return `
    <div class="c b lg">${t.station_name || "المطبخ"}</div>
    <div class="c md b">${head}</div>
    <div class="c sm">${time}${t.waiter_name ? " · " + t.waiter_name : ""}</div>
    <hr/>
    <table>${items}</table>
    ${t.notes ? `<hr/><div class="sm">${t.notes}</div>` : ""}
    <hr/>
    <div class="c sm">— تذكرة مطبخ —</div>`;
}

export function receiptHtml({ company, tab, total, currency, invoice_number, guestLabel, shareAmount }) {
  const head = tab.order_type === "dine_in" ? `طاولة ${tab.table_number || "-"}` : typeLabel(tab.order_type);
  const cust = [tab.customer_name, tab.customer_phone].filter(Boolean).join(" · ");
  // Compact "share" slip (equal split) — no item lines, just the amount due for this guest.
  if (shareAmount != null) {
    return `
      <div class="c b lg">${company?.name || "مطعم"}</div>
      <hr/>
      <div class="c md b">${head}</div>
      ${guestLabel ? `<div class="c md b">${guestLabel}</div>` : ""}
      <div class="c sm">${new Date().toLocaleString("ar-EG")}</div>
      <hr/>
      <table><tr><td class="b md">الحصة</td><td class="rt b md">${Number(shareAmount).toLocaleString("ar-EG")} ${currency}</td></tr></table>
      <hr/>
      <div class="c sm">شكراً لزيارتكم 🌟</div>`;
  }
  const lines = (tab.lines || []).map((l) => {
    const lt = (l.qty || 0) * (l.price || 0);
    return `<tr><td class="qty">${l.qty}×</td><td>${l.name}${l.notes ? `<div class="note">↳ ${l.notes}</div>` : ""}</td><td class="rt">${lt.toLocaleString("ar-EG")}</td></tr>`;
  }).join("");
  return `
    <div class="c b lg">${company?.name || "مطعم"}</div>
    ${company?.phone ? `<div class="c sm">${company.phone}</div>` : ""}
    ${company?.address ? `<div class="c sm">${company.address}</div>` : ""}
    <hr/>
    <div class="c md b">${head}</div>
    ${guestLabel ? `<div class="c md b">${guestLabel}</div>` : ""}
    ${cust ? `<div class="c sm">${cust}</div>` : ""}
    ${tab.customer_address ? `<div class="c sm">${tab.customer_address}</div>` : ""}
    ${invoice_number ? `<div class="c sm">فاتورة #${invoice_number}</div>` : ""}
    <div class="c sm">${new Date().toLocaleString("ar-EG")}</div>
    <hr/>
    <table>${lines}</table>
    <hr/>
    <table><tr><td class="b md">الإجمالي</td><td class="rt b md">${Number(total).toLocaleString("ar-EG")} ${currency}</td></tr></table>
    <hr/>
    <div class="c sm">شكراً لزيارتكم 🌟</div>`;
}

function kitchenTicketBytes(t) {
  const parts = [encoder.init(), encoder.align("center"), encoder.bold(true), encoder.size(true),
    encoder.text((t.station_name || "المطبخ") + "\n"), encoder.size(false),
    encoder.text((t.order_type === "dine_in" ? `طاولة ${t.table_number || "-"}` : typeLabel(t.order_type)) + "\n"),
    encoder.bold(false), encoder.align("left"), encoder.feed(1)];
  for (const it of (t.items || [])) {
    parts.push(encoder.bold(true), encoder.text(`${it.qty}x ${it.name}\n`), encoder.bold(false));
    if (it.notes) parts.push(encoder.text(`   - ${it.notes}\n`));
  }
  parts.push(encoder.feed(3), encoder.cut());
  return parts;
}

async function usbWrite(stationKey, parts) {
  const total = parts.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total); let off = 0;
  for (const a of parts) { out.set(a, off); off += a.length; }
  await writeBytesTo(stationKey, out);
}

/**
 * Print a batch of station tickets. USB-bound stations print silently to their
 * printer; the rest are combined into one 80mm print window (default printer).
 */
export async function printKitchenTickets(tickets, cid) {
  const printers = getStationPrinters(cid);
  const html = [];
  for (const t of tickets) {
    const key = t.station_id || "_default";
    const cfg = printers[key] || {};
    if (cfg.mode === "usb" && serialSupported()) {
      try { await usbWrite(key, kitchenTicketBytes(t)); continue; }
      catch (e) { /* fall through to browser print */ }
    }
    html.push(t);
  }
  if (html.length) {
    const body = html.map((t) => `<div style="page-break-after:always">${kitchenTicketHtml(t)}</div>`).join("");
    openPrintWindow("تذاكر المطبخ", body);
  }
}

export function printReceipt(args) {
  openPrintWindow("إيصال", receiptHtml(args));
}

/** Print several receipts (e.g. one per guest when splitting a bill) in a single window. */
export function printReceipts(list) {
  const arr = (list || []).filter(Boolean);
  if (!arr.length) return;
  const body = arr.map((a) => `<div style="page-break-after:always">${receiptHtml(a)}</div>`).join("");
  openPrintWindow("إيصالات", body);
}
